// Dock section listing unsent leads with a self-perform "Send all" CTA.
// Replaces the unsent-badge row that used to live in
// `LeadsModeBanners.tsx` — Slice D of
// docs/planning/activity_dock_workflow_facilitation.md §6.
//
// Reads `leads` from `WorkspaceUrgencyContext` (already the
// location-scoped workspace pool — see `LeadWorkspaceShell.tsx`'s
// `WorkspaceUrgencyProvider` call), filters for `!sent && !isTest`,
// and renders nothing when there are zero unsent leads so the dock's
// expanded panel stays calm during normal operation.
//
// "Send all" delegates to `onSendAllUnsent`, which the shell wires to
// a sequential `sendToGHL` loop over the same filtered set. Mirrors
// the existing `scheduleRetryQueued` loop in `useGhlSender` but
// targets *all* unsent leads, not just the `queued` retry subset.
// Disabled while another send is in flight (`isSending`) so a rapid
// double-click can't fan out into overlapping send locks.

import { useWorkspaceUrgencyContext } from "@/contexts/WorkspaceUrgencyContext";
import type { Lead } from "@/types/lead";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { formatUnsentBodyLine } from "./unsentLeadsCopy";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export type ActivityDockUnsentLeadsSectionProps = {
  onSendAllUnsent?: () => void | Promise<void>;
  isSending?: boolean;
  /**
   * Slice B2.5 — current workspace primary mode, used only to shape
   * the section's body line. Visibility / role-gating remain
   * unchanged: this is a copy lever, not a render gate. `null` /
   * `undefined` falls back to the brand-neutral default copy.
   */
  currentMode?: PrimaryMode | null;
};

const PREVIEW_MAX = 5;

function leadPreviewLabel(lead: Lead): string {
  const first = lead.extracted?.first_name?.trim() ?? "";
  const last = lead.extracted?.last_name?.trim() ?? "";
  const name = `${first} ${last}`.trim();
  if (name) return name;
  const phone = lead.extracted?.phone?.trim();
  if (phone) return phone;
  return "Untitled lead";
}

export function ActivityDockUnsentLeadsSection({
  onSendAllUnsent,
  isSending = false,
  currentMode = null,
}: ActivityDockUnsentLeadsSectionProps) {
  const urgency = useWorkspaceUrgencyContext();
  const allLeads = urgency?.leads ?? [];
  const unsentLeads = allLeads.filter((l) => !l.sent && !l.isTest);
  const totalUnsent = unsentLeads.length;

  if (totalUnsent === 0) return null;

  const bodyLine = formatUnsentBodyLine(totalUnsent, currentMode);

  const recent = [...unsentLeads]
    .sort((a, b) => b.lastUpdated - a.lastUpdated)
    .slice(0, PREVIEW_MAX);
  const overflow = totalUnsent - recent.length;
  const toneClass =
    totalUnsent >= 5
      ? "border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100"
      : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100";

  return (
    <section
      {...{ [FIREFLY_TARGET_ATTR]: "unsent-leads-section" }}
      className={
        "mt-3 rounded-2xl border px-3 py-3 text-left text-xs " + toneClass
      }
      aria-label={`${totalUnsent} unsent lead${totalUnsent === 1 ? "" : "s"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
            Unsent leads
          </div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums">
            {bodyLine}
          </div>
        </div>
        {onSendAllUnsent ? (
          <button
            type="button"
            {...{ [FIREFLY_TARGET_ATTR]: "send-all-unsent" }}
            disabled={isSending}
            onClick={() => void onSendAllUnsent()}
            className={
              "shrink-0 rounded-lg border border-current bg-white/70 px-2.5 py-1 text-[11px] font-semibold " +
              "transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed " +
              "dark:bg-zinc-900/60"
            }
          >
            {isSending ? "Sending…" : "Send all"}
          </button>
        ) : null}
      </div>
      <ul className="mt-2 space-y-1">
        {recent.map((lead) => (
          <li
            key={lead.id}
            className="flex items-center justify-between gap-2 truncate text-[11px]"
          >
            <span className="truncate">{leadPreviewLabel(lead)}</span>
            {lead.queued ? (
              <span className="shrink-0 rounded-full bg-white/60 px-1.5 text-[10px] font-medium opacity-80 dark:bg-zinc-800/60">
                queued
              </span>
            ) : null}
          </li>
        ))}
        {overflow > 0 ? (
          <li className="text-[11px] opacity-75">
            …and {overflow} more
          </li>
        ) : null}
      </ul>
    </section>
  );
}
